// src/padplanner/templateVocabulary.js
//
// The template library's words and shapes, shared by the plan page's
// Templates tab and the library page.
//
// ── DISPLAY GROUPING IS NOT STORAGE (S3 decision D7) ───────────────────────
//
// A template stores one of design §5.2's categories: 149's ten, 160's
// manifold, power and control, and 163's chemical, wireline and fuel. Mockup 4
// shows fewer, grouped sections ("Tanks & sand", "Barriers & containment");
// this map groups the sixteen onto them,
// in the mockup's order, and adds the sections the mockup's scroll does not
// reach. Every category is in exactly one group
// (templateVocabulary.test.js asserts it), so no template can fall out of
// the tab.
//
// ── PREVIEWS ARE SHAPES, NOT ICONS ─────────────────────────────────────────
//
// A footprint tile draws the template's own outline (local feet, SRID 0;
// length along +Y, design §7.3), scaled to fit the tile. A zone is its circle,
// a line a stroke, a pattern its dots. Nothing is drawn from sample art: under
// real-or-cut the preview is the stored shape or nothing.

// Migration 163's sixteen, as admin-api/lib/padplanner/templateCategories.js
// has them (ruleVocabulary.test.js holds the two equal).
export const TEMPLATE_CATEGORIES = Object.freeze([
  "wellhead", "frac_fleet", "manifold", "tank", "sand", "chemical", "flowback",
  "wireline", "barrier", "zone", "line", "facility", "power", "fuel", "control",
  "annotation",
]);

export const CATEGORY_LABELS = Object.freeze({
  wellhead: "Wellhead", frac_fleet: "Frac fleet", manifold: "Manifold", tank: "Tank", sand: "Sand",
  chemical: "Chemical", flowback: "Flowback", wireline: "Wireline", barrier: "Barrier", zone: "Zone",
  line: "Line", facility: "Facility", power: "Power", fuel: "Fuel", control: "Control", annotation: "Annotation",
});

export const TEMPLATE_GROUPS = Object.freeze([
  { key: "wellheads", label: "Wellheads", categories: ["wellhead"] },
  { key: "frac_fleet", label: "Frac fleet", categories: ["frac_fleet"] },
  { key: "manifold", label: "Manifold & iron", categories: ["manifold"] },
  { key: "tanks_sand", label: "Tanks & sand", categories: ["tank", "sand"] },
  { key: "chemical", label: "Chemical & fluid", categories: ["chemical"] },
  { key: "flowback", label: "Flowback", categories: ["flowback"] },
  { key: "wireline", label: "Wireline & CT", categories: ["wireline"] },
  { key: "barriers", label: "Barriers & containment", categories: ["barrier"] },
  { key: "zones_lines", label: "Zones & lines", categories: ["zone", "line"] },
  { key: "facilities", label: "Facilities", categories: ["facility"] },
  { key: "power", label: "Power", categories: ["power"] },
  { key: "fuel", label: "Fuel & gas", categories: ["fuel"] },
  { key: "control", label: "Control", categories: ["control"] },
  { key: "annotations", label: "Annotations", categories: ["annotation"] },
]);

// Preview colors are MAP-DATA colors, like PadPlannerMap's layer paints,
// not chrome tokens (accent-blue is retired from chrome; accentRoles.test.js).
// The footprint is the plan perimeter layer's sky; a zone is drawn dashed
// orange as mockup 4 draws its redzones; a line red as its flow lines.
export const PREVIEW_COLORS = Object.freeze({ footprint: "#38bdf8", zone: "#f97316", line: "#ef4444" });

export const NO_LIBRARY_TEXT = "No template library yet";
export const NO_SET_PUBLISHED_TEXT = "No template set is published yet, not even the AI PRIME standard set.";
export const PLACING_LATER_TEXT = "Placing templates on the map comes with the plan editor.";
export const NO_SOURCE_TEXT = "No source cited";

/** The non-empty groups, in display order, each with its templates in stored order. */
export function groupTemplates(templates) {
  return TEMPLATE_GROUPS
    .map((g) => ({ ...g, templates: templates.filter((t) => g.categories.includes(t.category)) }))
    .filter((g) => g.templates.length > 0);
}

/** Case-insensitive match on name, key and category (its words, not its code). */
export function matchesSearch(t, q) {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return [t.name, t.key, CATEGORY_LABELS[t.category] ?? t.category]
    .some((s) => String(s ?? "").toLowerCase().includes(needle));
}

const feet = (n) => `${Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 })} ft`;

/** What a template measures, in words: "45 ft × 8 ft", "50 ft radius", "Style only". */
export function templateDims(t) {
  if (t.kind === "footprint" && t.length_ft != null && t.width_ft != null) return `${feet(t.length_ft)} × ${feet(t.width_ft)}`;
  if (t.kind === "zone" && t.radius_ft != null) return `${feet(t.radius_ft)} radius`;
  if (t.kind === "line") return "Style only";
  if (t.kind === "pattern") return "Pattern";
  return "";
}

/** "Stim Stencils · v3". */
export function setChipLabel(set) {
  return `${set.name} · v${set.version}`;
}

export function feetText(n) {
  return n == null ? null : feet(n);
}

/**
 * An SVG path for a footprint (GeoJSON Polygon in feet), fitted into a
 * w x h box with `pad` on each side, aspect kept, +Y up. Null when there is
 * no ring to draw.
 */
export function footprintPath(geojson, { w = 44, h = 28, pad = 2 } = {}) {
  const ring = geojson?.type === "Polygon" ? geojson.coordinates?.[0] : null;
  if (!Array.isArray(ring) || ring.length < 4) return null;
  const xs = ring.map((p) => p[0]);
  const ys = ring.map((p) => p[1]);
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const scale = Math.min((w - 2 * pad) / (x1 - x0 || 1), (h - 2 * pad) / (y1 - y0 || 1));
  const ox = (w - (x1 - x0) * scale) / 2;
  const oy = (h - (y1 - y0) * scale) / 2;
  const pt = ([x, y]) => `${(ox + (x - x0) * scale).toFixed(2)} ${(h - oy - (y - y0) * scale).toFixed(2)}`;
  return `M${ring.slice(0, -1).map(pt).join("L")}Z`;
}
